import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchBar from './SearchBar';
import { importEpisode, requestEpisodeUploadUrl, resolveEpisodeUrl, searchEpisodes, uploadTranscript, getYouTubeMetadata, importYouTubeVideo, isYouTubeUrl } from '../../../lib/apiClient';
import { supabase } from '../../../supabase/supabaseClient';

const SEARCH_DEBOUNCE = 350;
export default function ImportEpisodeModal({ isOpen, onClose, onConfirm, projectId }) {
  const [activeTab, setActiveTab] = useState('search'); // search | url | upload | transcript
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchState, setSearchState] = useState({ loading: false, error: null });
  const [selected, setSelected] = useState(null);
  const [pastedUrl, setPastedUrl] = useState('');
  const [resolvedUrlMeta, setResolvedUrlMeta] = useState(null);
  const [urlState, setUrlState] = useState({ loading: false, error: null });
  // YouTube-specific state
  const [isYouTube, setIsYouTube] = useState(false);
  const [youtubeMetadata, setYoutubeMetadata] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDetails, setUploadDetails] = useState(null);
  // Transcript upload state
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [transcriptFormat, setTranscriptFormat] = useState(null); // 'txt'|'vtt'|'srt'
  const [transcriptTitle, setTranscriptTitle] = useState('');
  const [transcriptContent, setTranscriptContent] = useState('');
  const [transcriptState, setTranscriptState] = useState({ loading: false, error: null });
  const [uploadState, setUploadState] = useState({ loading: false, error: null });
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('search');
      setQuery('');
      setResults([]);
      setSelected(null);
      setPastedUrl('');
      setResolvedUrlMeta(null);
      setUrlState({ loading: false, error: null });
      setIsYouTube(false);
      setYoutubeMetadata(null);
      setFileInfo(null);
      setSelectedFile(null);
      setUploadDetails(null);
      setUploadState({ loading: false, error: null });
      setConfirmError(null);
      setSearchState({ loading: false, error: null });
      setConfirming(false);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    // Reset transcript state when closing
    setTranscriptFile(null);
    setTranscriptFormat(null);
    setTranscriptTitle('');
    setTranscriptContent('');
    setTranscriptState({ loading: false, error: null });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query) {
      setResults([]);
      setSearchState({ loading: false, error: null });
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setSearchState({ loading: true, error: null });

    const t = setTimeout(async () => {
      try {
        const data = await searchEpisodes(query, { limit: 25, signal: controller.signal });
        setResults(data?.results || []);
        setSearchState({ loading: false, error: null });
      } catch (error) {
        if (controller.signal.aborted) return;
        setSearchState({ loading: false, error: error.message || 'Search failed' });
      }
    }, SEARCH_DEBOUNCE);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  const handleTranscriptSelection = async (file) => {
    setTranscriptFile(null);
    setTranscriptContent('');
    setTranscriptFormat(null);
    setTranscriptTitle('');
    setTranscriptState({ loading: true, error: null });
    try {
      if (!file) throw new Error('No file selected');
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!['txt','vtt','srt'].includes(ext)) {
        throw new Error('Unsupported transcript format. Allowed: .txt, .vtt, .srt');
      }
      const text = await file.text();
      if (!text.trim()) throw new Error('Empty file');
      const derivedTitle = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'Imported Transcript';
      setTranscriptFile(file);
      setTranscriptContent(text);
      setTranscriptFormat(ext);
      setTranscriptTitle(derivedTitle);
      setTranscriptState({ loading: false, error: null });
    } catch (err) {
      setTranscriptFile(null);
      setTranscriptContent('');
      setTranscriptFormat(null);
      setTranscriptTitle('');
      setTranscriptState({ loading: false, error: err.message || 'Failed to read file' });
    }
  };

  const canConfirm = useMemo(() => {
    if (activeTab === 'search') return !!selected;
    if (activeTab === 'url') {
      // For YouTube URLs, check youtubeMetadata; for others, check resolvedUrlMeta
      if (isYouTube) return !!youtubeMetadata && !urlState.loading;
      return !!resolvedUrlMeta && !urlState.loading;
    }
    if (activeTab === 'upload') return !!fileInfo && !uploadState.loading;
    if (activeTab === 'transcript') return !!transcriptContent && !transcriptState.loading;
    return false;
  }, [activeTab, selected, resolvedUrlMeta, urlState.loading, fileInfo, uploadState.loading, transcriptContent, transcriptState.loading, isYouTube, youtubeMetadata]);

  useEffect(() => {
    if (activeTab !== 'url') {
      setResolvedUrlMeta(null);
      setUrlState({ loading: false, error: null });
    }
  }, [activeTab]);

  const handleResolveUrl = async () => {
    if (!pastedUrl || !isValidUrl(pastedUrl)) {
      setUrlState({ loading: false, error: 'Enter a valid URL' });
      return;
    }
    
    const trimmedUrl = pastedUrl.trim();
    const youtubeDetected = isYouTubeUrl(trimmedUrl);
    setIsYouTube(youtubeDetected);
    setUrlState({ loading: true, error: null });
    setYoutubeMetadata(null);
    setResolvedUrlMeta(null);
    
    try {
      if (youtubeDetected) {
        // Handle YouTube URL
        const data = await getYouTubeMetadata(trimmedUrl, { projectId });
        if (data?.video) {
          setYoutubeMetadata(data.video);
          setUrlState({ loading: false, error: null });
          setConfirmError(null);
        } else {
          throw new Error('Could not fetch video metadata');
        }
      } else {
        // Handle regular podcast URL
        const data = await resolveEpisodeUrl(trimmedUrl);
        setResolvedUrlMeta(data?.episode || null);
        setUrlState({ loading: false, error: null });
        setConfirmError(null);
      }
    } catch (error) {
      setResolvedUrlMeta(null);
      setYoutubeMetadata(null);
      setUrlState({ loading: false, error: error.message || 'Failed to resolve URL' });
    }
  };

  const handleUploadSelection = async (file) => {
    setFileInfo(null);
    setSelectedFile(null);
    setUploadDetails(null);
    setUploadState({ loading: true, error: null });
    try {
      if (!file) throw new Error('No file selected');
      if (!projectId) {
        throw new Error('Project ID is required to upload episodes');
      }

      const extension = (file.name.split('.').pop() || '').toLowerCase();

      const {
        uploadUrl,
        token,
        fileId,
        bucket,
        episodeId,
        storagePath,
        allowedFormats,
        maxFileSize
      } = await requestEpisodeUploadUrl({
        fileName: file.name,
        contentType: file.type || 'audio/mpeg',
        projectId,
      });
      if (!uploadUrl || !token) {
        throw new Error('Failed to obtain upload URL');
      }

      setSelectedFile(file);
      setUploadDetails({
        uploadUrl,
        token,
        fileId,
        bucket,
        episodeId,
        storagePath,
      });
      setFileInfo({
        name: file.name,
        size: file.size,
        bucket,
        episodeId,
        storagePath,
        fileFormat: extension,
        allowedFormats,
        maxFileSize,
        uploaded: false
      });
      setUploadState({ loading: false, error: null });
    } catch (error) {
      setFileInfo(null);
      setSelectedFile(null);
      setUploadDetails(null);
      setUploadState({ loading: false, error: error.message || 'Upload failed' });
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm || confirming) return;
    setConfirming(true);
    setConfirmError(null);

    try {
      let payload;
      if (activeTab === 'search' && selected) {
        payload = {
          source: 'podcastindex',
          enclosureUrl: selected.enclosureUrl,
          metadata: selected,
        };
      } else if (activeTab === 'url' && isYouTube && youtubeMetadata) {
        // Handle YouTube import
        if (!projectId) {
          throw new Error('Project ID is required to import YouTube videos');
        }
        
        const { jobId, episodeId, status } = await importYouTubeVideo(
          pastedUrl.trim(),
          youtubeMetadata,
          { projectId }
        );
        
        if (!jobId || !episodeId) {
          throw new Error('YouTube import response missing identifiers');
        }

        onConfirm(
          {
            title: youtubeMetadata.title || 'YouTube Import',
            show: youtubeMetadata.uploader || 'YouTube',
            pubDate: youtubeMetadata.uploadDate || null,
            durationSec: youtubeMetadata.duration || null,
            image: youtubeMetadata.thumbnail || null,
            enclosureUrl: youtubeMetadata.url || pastedUrl,
            feedUrl: null,
          },
          { jobId, episodeId, stage: status || 'extracting', source: 'youtube' }
        );

        onClose();
        return;
      } else if (activeTab === 'url' && resolvedUrlMeta) {
        payload = {
          source: 'direct_url',
          enclosureUrl: resolvedUrlMeta.enclosureUrl,
          metadata: resolvedUrlMeta,
          originalUrl: pastedUrl,
        };
      } else if (activeTab === 'upload' && fileInfo) {
        if (!selectedFile || !uploadDetails) {
          throw new Error('Upload not prepared. Please reselect the file.');
        }

        if (!fileInfo.uploaded) {
          try {
            setUploadState({ loading: true, error: null });

            const sdkResult = await supabase.storage
              .from('episodes')
              .uploadToSignedUrl(uploadDetails.storagePath, uploadDetails.token, selectedFile);

            if (sdkResult.error) {
              console.error('[EpisodesUpload] Supabase SDK upload failed', {
                error: sdkResult.error?.message,
                storagePath: uploadDetails.storagePath,
                bucket: uploadDetails.bucket,
                episodeId: uploadDetails.episodeId,
              });
              throw new Error(`Upload failed: ${sdkResult.error.message}`);
            }

            console.info('[EpisodesUpload] Supabase SDK upload succeeded', {
              storagePath: uploadDetails.storagePath,
              bucket: uploadDetails.bucket,
              episodeId: uploadDetails.episodeId,
            });

            setFileInfo((prev) => (prev ? { ...prev, uploaded: true } : prev));
            setUploadState({ loading: false, error: null });
          } catch (uploadError) {
            console.error('[EpisodesUpload] Upload exception', uploadError);
            setUploadState({ loading: false, error: uploadError.message || 'Upload failed' });
            throw uploadError;
          }
        }

        payload = {
          source: 'file',
          episodeId: uploadDetails.episodeId,
          fileId: uploadDetails.fileId,
          storagePath: uploadDetails.storagePath,
          metadata: {
            title: fileInfo.name,
            durationSec: null,
            image: null,
            fileSize: fileInfo.size,
            fileFormat: fileInfo.fileFormat,
            originalFilename: fileInfo.name,
          },
        };
      } else if (activeTab === 'transcript' && transcriptContent) {
        if (!projectId) {
          throw new Error('Project ID is required to import episodes');
        }
        // Directly invoke transcript ingestion endpoint and return early
        const resp = await uploadTranscript({
          projectId,
          title: transcriptTitle,
          language: 'auto',
          format: transcriptFormat,
          content: transcriptContent,
        });

        const meta = {
          title: transcriptTitle || 'Imported Transcript',
          show: 'Direct upload',
          durationSec: null,
          image: null,
        };

        onConfirm(
          {
            title: meta.title,
            show: meta.show,
            pubDate: null,
            durationSec: null,
            image: null,
            enclosureUrl: null,
            feedUrl: null,
          },
          { jobId: resp?.jobId || null, episodeId: resp?.episodeId, stage: 'ready', source: 'transcript_upload' }
        );

        onClose();
        return;
      }

      if (!payload) {
        throw new Error('Missing import payload');
      }

      if (!projectId) {
        throw new Error('Project ID is required to import episodes');
      }

      const { jobId, episodeId, status } = await importEpisode(payload, { projectId });
      if (!jobId || !episodeId) {
        throw new Error('Import response missing identifiers');
      }

      const meta = selected || resolvedUrlMeta || {
        title: fileInfo?.name,
        show: 'Uploaded file',
        durationSec: null,
        image: null,
      };

      onConfirm(
        {
          title: meta.title || 'Imported episode',
          show: meta.show || meta.show_title || 'Imported show',
          pubDate: meta.pubDate || meta.pub_date || null,
          durationSec: meta.durationSec || meta.duration_sec || null,
          image: meta.image || meta.image_url || null,
          enclosureUrl: meta.enclosureUrl || meta.enclosure_url || payload.enclosureUrl || null,
          feedUrl: meta.feedUrl || meta.feed_url || null,
        },
        { jobId, episodeId, stage: status || 'queued', source: payload.source }
      );

      onClose();
    } catch (error) {
      setConfirmError(error.message || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-3">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-surface bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-surface px-5 py-3">
          <h2 className="text-lg font-semibold text-primary">Import episode</h2>
          <button onClick={onClose} className="rounded bg-surfaceHover px-3 py-1 text-sm text-textSecondary hover:bg-surface">Close</button>
        </div>

        <div className="flex gap-2 border-b border-surface px-5 py-2 text-sm">
          <TabButton id="search" active={activeTab} setActive={setActiveTab}>
            Search
          </TabButton>
          <TabButton id="url" active={activeTab} setActive={setActiveTab}>
            Paste URL
          </TabButton>
          <TabButton id="upload" active={activeTab} setActive={setActiveTab}>
            Upload
          </TabButton>
          <TabButton id="transcript" active={activeTab} setActive={setActiveTab}>
            Transcript
          </TabButton>
        </div>

        <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
          <div className="p-5">
            {activeTab === 'search' && (
              <div className="space-y-3">
                <SearchBar value={query} onChange={setQuery} placeholder="Search PodcastIndex…" />
                {searchState.loading && <div className="text-xs text-textMuted">Searching…</div>}
                {searchState.error && <div className="text-xs text-accent">{searchState.error}</div>}
                <div className="max-h-80 space-y-2 overflow-y-auto">
                  {results.map((r) => (
                    <button
                      key={r.episodeId}
                      onClick={() => setSelected(r)}
                      className={`flex w-full gap-3 rounded-lg border p-3 text-left hover:bg-surfaceHover ${
                        selected?.episodeId === r.episodeId ? 'border-primary' : 'border-surface'
                      }`}
                    >
                      <img alt="art" src={r.image} className="h-12 w-12 rounded object-cover" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-textPrimary">{r.title}</div>
                        <div className="truncate text-xs text-textMuted">{r.show} • {formatDate(r.pubDate)} • {formatDuration(r.durationSec)}</div>
                        <div className="mt-1 line-clamp-2 text-xs text-textSecondary">{r.descriptionSnippet}</div>
                      </div>
                    </button>
                  ))}
                  {!query && <div className="text-xs text-textMuted">Type to search PodcastIndex.</div>}
                  {query && !results.length && !searchState.loading && (
                    <div className="text-xs text-textMuted">No results yet. Try a different phrase.</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'transcript' && (
              <div className="space-y-3">
                <input
                  type="file"
                  accept=".txt,.vtt,.srt,text/plain,text/vtt,application/x-subrip"
                  onChange={(e) => handleTranscriptSelection(e.target.files?.[0] || null)}
                  className="w-full text-sm text-textSecondary file:mr-3 file:rounded-lg file:border file:border-surface file:bg-surfaceHover file:px-3 file:py-2 file:text-sm file:text-textSecondary hover:file:bg-surface"
                />
                {transcriptState.loading && <div className="text-xs text-textMuted">Reading transcript…</div>}
                {transcriptState.error && <div className="text-xs text-accent">{transcriptState.error}</div>}
                {transcriptFile && (
                  <div className="rounded border border-surface bg-background p-3 text-xs text-textSecondary">
                    File: <strong className="text-textPrimary">{transcriptFile.name}</strong>
                    <div className="mt-2">Detected format: {transcriptFormat?.toUpperCase()}</div>
                    <label className="mt-2 block text-xs text-textMuted">Episode title</label>
                    <input
                      value={transcriptTitle}
                      onChange={(e)=>setTranscriptTitle(e.target.value)}
                      className="mt-1 w-full rounded border border-surface bg-background p-2 text-sm text-textSecondary"
                    />
                    <div className="mt-2 line-clamp-3 text-textMuted">Preview: {transcriptContent.slice(0, 180)}</div>
                  </div>
                )}
                <div className="text-xs text-textMuted">Upload a transcript to skip transcription. We’ll chunk it and continue the pipeline.</div>
              </div>
            )}

            {activeTab === 'url' && (
              <div className="space-y-3">
                <input
                  value={pastedUrl}
                  onChange={(e) => setPastedUrl(e.target.value)}
                  onBlur={handleResolveUrl}
                  placeholder="Paste a podcast URL or YouTube link"
                  className="w-full rounded-lg border border-surface bg-background p-3 text-sm text-textSecondary placeholder:text-textMuted focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  onClick={handleResolveUrl}
                  disabled={urlState.loading || !pastedUrl}
                  className="rounded-lg border border-primary px-3 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-60"
                >
                  {urlState.loading ? 'Resolving…' : 'Resolve URL'}
                </button>
                {urlState.error && <div className="text-xs text-accent">{urlState.error}</div>}
                
                {/* YouTube metadata display */}
                {isYouTube && youtubeMetadata && (
                  <div className="rounded border border-red-500/30 bg-red-500/5 p-3 text-xs text-textSecondary">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">YouTube</span>
                      <span className="text-textMuted">{youtubeMetadata.platform}</span>
                    </div>
                    {youtubeMetadata.thumbnail && (
                      <img 
                        src={youtubeMetadata.thumbnail} 
                        alt={youtubeMetadata.title}
                        className="w-full h-32 object-cover rounded mb-2"
                      />
                    )}
                    <strong className="text-textPrimary block">{youtubeMetadata.title}</strong>
                    <div className="text-textMuted mt-1">{youtubeMetadata.uploader}</div>
                    <div className="flex gap-3 mt-2 text-textMuted">
                      {youtubeMetadata.durationFormatted && (
                        <span>Duration: {youtubeMetadata.durationFormatted}</span>
                      )}
                      {youtubeMetadata.viewCount > 0 && (
                        <span>{youtubeMetadata.viewCount.toLocaleString()} views</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Regular URL metadata display */}
                {!isYouTube && resolvedUrlMeta && (
                  <div className="rounded border border-surface bg-background p-3 text-xs text-textSecondary">
                    <strong className="text-textPrimary">{resolvedUrlMeta.title}</strong>
                    <div>{resolvedUrlMeta.show}</div>
                    <div className="text-textMuted">{resolvedUrlMeta.enclosureUrl}</div>
                  </div>
                )}
                
                <div className="text-xs text-textMuted">
                  {isYouTubeUrl(pastedUrl) 
                    ? 'YouTube video detected. Audio will be extracted and transcribed.'
                    : 'Paste a podcast episode URL or YouTube video link.'}
                </div>
              </div>
            )}

            {activeTab === 'upload' && (
              <div className="space-y-3">
                <input
                  type="file"
                  accept="audio/mpeg, audio/mp4, audio/x-m4a, audio/wav"
                  onChange={(e) => handleUploadSelection(e.target.files?.[0] || null)}
                  className="w-full text-sm text-textSecondary file:mr-3 file:rounded-lg file:border file:border-surface file:bg-surfaceHover file:px-3 file:py-2 file:text-sm file:text-textSecondary hover:file:bg-surface"
                />
                {uploadState.loading && <div className="text-xs text-textMuted">Uploading…</div>}
                {uploadState.error && <div className="text-xs text-accent">{uploadState.error}</div>}
                {fileInfo && (
                  <div className="rounded border border-surface bg-background p-3 text-xs text-textSecondary">
                    File: <strong className="text-textPrimary">{fileInfo.name}</strong> • Size: {formatBytes(fileInfo.size)}
                    {fileInfo.allowedFormats && (
                      <div className="mt-2">Allowed formats: {fileInfo.allowedFormats.join(', ')}</div>
                    )}
                    {fileInfo.maxFileSize && (
                      <div className="mt-1">Max size: {formatBytes(fileInfo.maxFileSize)}</div>
                    )}
                    {!fileInfo.uploaded && (
                      <div className="mt-2 text-textMuted">Audio uploads when you confirm.</div>
                    )}
                    {fileInfo.uploaded && (
                      <div className="mt-2 text-green-400">Upload ready.</div>
                    )}
                  </div>
                )}
                <div className="text-xs text-textMuted">This simulates the presigned upload flow.</div>
              </div>
            )}
          </div>

          <div className="border-t border-surface p-5 md:border-l md:border-t-0">
            <h3 className="text-sm font-semibold text-textPrimary">Confirm selection</h3>
            <div className="mt-3 rounded-lg border border-surface bg-background p-4 text-sm text-textSecondary">
              {activeTab === 'search' && selected && (
                <ConfirmEpisodeCard ep={selected} />
              )}
              {activeTab === 'url' && pastedUrl && isValidUrl(pastedUrl) && (
                <div>
                  {isYouTube && youtubeMetadata ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">YouTube</span>
                        <span className="text-xs text-textMuted">Video Import</span>
                      </div>
                      {youtubeMetadata.thumbnail && (
                        <img 
                          src={youtubeMetadata.thumbnail} 
                          alt={youtubeMetadata.title}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                      )}
                      <div className="text-textPrimary font-medium">{youtubeMetadata.title}</div>
                      <div className="text-xs text-textMuted mt-1">
                        {youtubeMetadata.uploader} • {youtubeMetadata.durationFormatted || formatDuration(youtubeMetadata.duration)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-textMuted">Episode URL</div>
                      <div className="break-all text-textPrimary">{pastedUrl}</div>
                      {resolvedUrlMeta && (
                        <div className="mt-2 text-xs text-textSecondary">
                          {resolvedUrlMeta.title} • {formatDate(resolvedUrlMeta.pubDate)} • {formatDuration(resolvedUrlMeta.durationSec)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {activeTab === 'upload' && fileInfo && (
                <div>
                  <div className="text-xs text-textMuted">Upload</div>
                  <div className="text-textPrimary">{fileInfo.name} • {formatBytes(fileInfo.size)}</div>
                </div>
              )}
              {!canConfirm && (
                <div className="text-xs text-textMuted">Select a result, resolve a valid URL, or upload a file to proceed.</div>
              )}
              {confirmError && <div className="text-xs text-accent">{confirmError}</div>}
            </div>
            <button
              disabled={!canConfirm || confirming}
              onClick={handleConfirm}
              className="mt-4 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-black hover:bg-emerald-600 disabled:opacity-60"
            >
              {confirming ? 'Importing…' : 'Import this episode'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ id, active, setActive, children }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => setActive(id)}
      className={`rounded px-3 py-1 ${isActive ? 'bg-primary text-black' : 'bg-surfaceHover text-textSecondary'}`}
    >
      {children}
    </button>
  );
}

function ConfirmEpisodeCard({ ep }) {
  return (
    <div className="flex gap-3">
      <img alt="art" src={ep.image} className="h-16 w-16 rounded object-cover" />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-textPrimary">{ep.title}</div>
        <div className="truncate text-xs text-textMuted">{ep.show} • {formatDate(ep.pubDate)} • {formatDuration(ep.durationSec)}</div>
        <div className="mt-1 line-clamp-2 text-xs text-textSecondary">{ep.descriptionSnippet}</div>
      </div>
    </div>
  );
}

function isValidUrl(url) {
  try {
    const u = new URL(url);
    return !!u.protocol && !!u.host;
  } catch {
    return false;
  }
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

